import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { PDFDownloadLink } from '@react-pdf/renderer';
import AssignWorkersModal from '../components/AssignWorkersModal';
import AdditionalWorkTable from '../components/AdditionalWorkTable';
import ProjectDetailsPDF from '../components/ProjectDetailsPDF';
import ActivityCategoryBlock from '../components/ActivityCategoryBlock';
import authFetch from '../api';
import { useTranslation } from 'react-i18next';

const ProjectDetails = ({ projectId }) => {
  const { t } = useTranslation();
  
  // Translation placeholders for Assign Workers Modal
  const assignWorkersTranslations = {
    title: t('projectDetails.assignTitle'),
    searchPlaceholder: t('projectDetails.assignSearch'),
    selectAll: t('projectDetails.assignSelectAll'),
    clearAll: t('projectDetails.assignClearAll'),
    cancel: t('projectDetails.assignCancel'),
    assign: t('projectDetails.assignAssign')
  };
  
  const params = useParams();
  const fallbackId = window.location.pathname.split('/').filter(Boolean).pop();
  const effectiveProjectId = projectId || params.id || fallbackId;

  // --- State Hooks (hourLogDates and pagination related states at the top) ---
  const [hourLogDates, setHourLogDates] = useState([]);
  // Date range picker state for Working Hours Logs
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // Date filtering for Working Hours Logs
  const filteredDates = hourLogDates
    .filter(date => {
      const d = new Date(date);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;
      return (!start || d >= start) && (!end || d <= end);
    })
    .sort((a, b) => new Date(a) - new Date(b));


  const [activities, setActivities] = useState([]);
  const [availableActivities, setAvailableActivities] = useState([]);
  const [activityDropdownValue, setActivityDropdownValue] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [loading, setLoading] = useState(true);
  // assignedWorkers initialized from project data (see useEffect)
  // Pagination state for Working Hours Logs
  const [assignedWorkers, setAssignedWorkers] = useState([]);
  const [workingHours, setWorkingHours] = useState({});
  const [workerDetails, setWorkerDetails] = useState({});
  const [editedHours, setEditedHours] = useState({});
  const [showLogModal, setShowLogModal] = useState(false);
  const [dailyInput, setDailyInput] = useState({});
  const [projectData, setProjectData] = useState(null);
  const [assignedEmployeeDetails, setAssignedEmployeeDetails] = useState(null);
  const [showAddAdditionalModal, setShowAddAdditionalModal] = useState(false);
  // Additional Work state
  const [additionalWorks, setAdditionalWorks] = useState([]);
  const [newAdditionalWork, setNewAdditionalWork] = useState({ description: '', hours: '', comment: '' });
  // Project Photos state
  const [photos, setPhotos] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  // --- Calculation that depends on assignedWorkers and workingHours ---
  const selectedDatesTotalHours = assignedWorkers.reduce((total, workerId) => {
    const logs = workingHours[workerId] || {};
    return total + filteredDates.reduce((sum, date) => sum + (logs[date] || 0), 0);
  }, 0);
  // Handle photo upload
  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    const formData = new FormData();
    if (effectiveProjectId) {
      console.log('🔍 Uploading photos for project:', effectiveProjectId);
      formData.append('projectId', effectiveProjectId);
    }
    if (!files.length) return;
    for (let i = 0; i < files.length; i++) {
      formData.append('images', files[i]);
    }
    console.log('📤 Uploading photos with FormData:', formData.get('projectId'), files.length, 'files');
    
    try {
      const res = await authFetch.post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      let data;
      if (res && res.data) {
        data = res.data;
      } else {
        data = res;
      }
      
      if (Array.isArray(data.imageUrls)) {
        console.log('✅ Photos uploaded successfully:', data.imageUrls.length, 'images');
        setPhotos(prev => [...prev, ...data.imageUrls]);
      } else {
        console.error('❌ Unexpected response format for photo upload:', data);
      }
    } catch (err) {
      console.error('❌ Error uploading photos:', err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
    }
  };

  useEffect(() => {
    if (!effectiveProjectId) {
      console.error('Project ID is missing.');
      setLoading(false);
      return;
    }
    
    const fetchData = async () => {
      try {
        console.log('🔍 Fetching project details for ID:', effectiveProjectId);
        
        // Check authentication
        const token = localStorage.getItem('jwtToken');
        if (!token) {
          console.error('❌ No authentication token found');
          setLoading(false);
          return;
        }
        
        // Fetch project data
        console.log('🔍 Fetching project data...');
        const projRes = await authFetch(`/projects/${effectiveProjectId}`);
        let projData;
        if (projRes && projRes.data) {
          projData = projRes.data;
        } else {
          projData = projRes;
        }
        console.log('📊 Project data received:', projData);
        setProjectData(projData);
        
        // Fetch assigned employee details
        if (projData.assignedEmployees && projData.assignedEmployees.length > 0) {
          try {
            console.log('🔍 Fetching employee details...');
            const response = await authFetch('/users');
            let allUsers;
            if (response && response.data) {
              allUsers = response.data;
            } else {
              allUsers = response;
            }
            console.log('📊 Users data received:', allUsers);
            
            const matchedNames = projData.assignedEmployees
              .map(id => allUsers.users?.find(u => u._id === id)?.name)
              .filter(Boolean);
            setAssignedEmployeeDetails(matchedNames.length > 0 ? matchedNames.join(', ') : '—');
          } catch (e) {
            console.error("Error fetching employee names:", e);
            setAssignedEmployeeDetails('—');
          }
        } else if (projData.assignedEmployee) {
          try {
            const res = await authFetch(`/users/${projData.assignedEmployee}`);
            let data;
            if (res && res.data) {
              data = res.data;
            } else {
              data = res;
            }
            const name = data?.name || data?.user?.name;
            setAssignedEmployeeDetails(name || '—');
          } catch (e) {
            console.error("Error fetching single employee:", e);
            setAssignedEmployeeDetails('—');
          }
        }
        
        // Fetch available activities/resources
        console.log('🔍 Fetching available activities...');
        const availRes = await authFetch('/resources/');
        let availData;
        if (availRes && availRes.data) {
          availData = availRes.data;
        } else {
          availData = availRes;
        }
        console.log('📊 Available activities received:', availData);

        // Process activities
        const initActivities = (projData.activities || []).map(act => ({
          ...act,
          remaining: act.planned - act.completed,
          progress: act.planned > 0 ? (act.completed / act.planned) * 100 : 0,
        }));
        console.log('📊 Processed activities:', initActivities);
        setActivities(initActivities);
        setAvailableActivities(availData || []);
        setAssignedWorkers(projData.assignedWorkers || []);
        
        // Fetch worker details for assigned workers
        if (projData.assignedWorkers && projData.assignedWorkers.length > 0) {
          try {
            console.log('🔍 Fetching worker details...');
            const workerRes = await authFetch("/workers");
            let allWorkers;
            if (workerRes && workerRes.data) {
              allWorkers = workerRes.data;
            } else {
              allWorkers = workerRes;
            }
            console.log('📊 Workers data received:', allWorkers);
            
            const details = {};
            (projData.assignedWorkers || []).forEach(id => {
              const found = allWorkers.find(w => w._id === id);
              if (found) details[id] = found;
            });
            setWorkerDetails(details);
          } catch (e) {
            console.error("Error fetching worker details:", e);
            setWorkerDetails({});
          }
        }

        // Fetch working hours
        try {
          console.log('🔍 Fetching working hours...');
          const hoursRes = await authFetch(`/working-hours?projectId=${effectiveProjectId}`);
          let hoursData;
          if (hoursRes && hoursRes.data) {
            hoursData = hoursRes.data;
          } else {
            hoursData = hoursRes;
          }
          console.log('📊 Working hours received:', hoursData);

          const grouped = {};
          const allDates = new Set();
          hoursData.forEach(entry => {
            if (!grouped[entry.workerId]) grouped[entry.workerId] = {};
            grouped[entry.workerId][entry.date] = entry.hoursWorked;
            allDates.add(entry.date);
          });
          setWorkingHours(grouped);
          setHourLogDates(Array.from(allDates).sort());
        } catch (e) {
          console.error("Error fetching working hours:", e);
          setWorkingHours({});
          setHourLogDates([]);
        }

        // Fetch additional work entries
        try {
          console.log('🔍 Fetching additional work...');
          const additionalRes = await authFetch(`/additionalwork?projectId=${effectiveProjectId}`);
          let additionalData;
          if (additionalRes && additionalRes.data) {
            additionalData = additionalRes.data;
          } else {
            additionalData = additionalRes;
          }
          console.log('📊 Additional work received:', additionalData);
          setAdditionalWorks(additionalData || []);
        } catch (e) {
          console.error("Error fetching additional work:", e);
          setAdditionalWorks([]);
        }

        // Fetch project photos
        try {
          console.log('🔍 Fetching project photos...');
          const photosRes = await authFetch(`/project-photos?projectId=${effectiveProjectId}`);
          let photoData;
          if (photosRes && photosRes.data) {
            photoData = photosRes.data;
          } else {
            photoData = photosRes;
          }
          console.log('📊 Project photos received:', photoData);
          setPhotos(photoData || []);
        } catch (e) {
          console.error("Error fetching project photos:", e);
          setPhotos([]);
        }

      } catch (err) {
        console.error('❌ Error fetching project data:', err);
        console.error('Error details:', {
          message: err.message,
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data
        });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [effectiveProjectId]);

  const handleAddActivity = (newActivity) => {
    setActivities(prev => [
      ...prev,
      {
        _id: Date.now().toString(),
        name: newActivity.name,
        unit: '',
        planned: newActivity.planned || 0,
        dailyTarget: newActivity.dailyTarget || 0,
        completed: newActivity.completed || 0,
        remaining: (newActivity.planned || 0) - (newActivity.completed || 0),
        progress: newActivity.planned > 0 ? ((newActivity.completed || 0) / newActivity.planned) * 100 : 0,
        category: newActivity.category || 'Mechanical',
        resourceId: newActivity.resourceId,
        dailyLog: newActivity.dailyLog || []
      }
    ]);
  };

  const handleUpdateActivity = (index, updatedActivity) => {
    setActivities(prev => prev.map((act, idx) => {
      if (idx !== index) return act;
      const updated = { ...act, ...updatedActivity };
      updated.remaining = updated.planned - updated.completed;
      updated.progress = updated.planned > 0 ? (updated.completed / updated.planned) * 100 : 0;
      return updated;
    }));
  };

  const handleDeleteActivity = (index) => {
    setActivities(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleActivityChange = (index, field, value) => {
    setActivities(prev => prev.map((act, idx) => {
      if (idx !== index) return act;
      const updated = { ...act, [field]: ['planned','dailyTarget','completed'].includes(field) ? Number(value) : value };
      updated.remaining = updated.planned - updated.completed;
      updated.progress = updated.planned > 0 ? (updated.completed / updated.planned) * 100 : 0;

      // Auto-fill dailyTarget based on working days if 'planned' is updated and dueDate exists
      if (field === 'planned' && projectData?.dueDate) {
        const today = new Date();
        const end = new Date(projectData.dueDate);
        let count = 0;
        const date = new Date(today);
        while (date <= end) {
          const day = date.getDay();
          if (day !== 0) count++; // Sunday off
          date.setDate(date.getDate() + 1);
        }
        updated.dailyTarget = count > 0 ? Math.ceil(updated.planned / count) : 0;
      }

      return updated;
    }));
  };

  const handleSaveActivities = async () => {
    try {
      console.log('🔍 Saving activities for project:', effectiveProjectId);
      const response = await authFetch.post(`/projects/${effectiveProjectId}/save-activities`, {
        activities: activities.map(({ _id, name, unit, planned, dailyTarget, completed, progress, category, resourceId, dailyLog }) => ({
          _id,
          name,
          unit,
          planned,
          dailyTarget,
          completed,
          progress,
          category,
          resourceId,
          dailyLog
        }))
      });
      
      console.log('✅ Activities saved successfully');
      alert(t('projectDetails.activitiesSaved'));
      localStorage.setItem('forceReloadNotifications', '1');
      
      // Refresh notification list after saving activities
      try {
        await authFetch.get('/notifications/all');
        console.log('[DEBUG] Notifications refetched after activity save.');
      } catch (err) {
        console.error('[ERROR] Failed to refetch notifications:', err);
      }
    } catch (err) {
      console.error('❌ Error saving activities:', err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
      alert(t('projectDetails.errorSavingActivities'));
    }
  };

  const handleSaveHours = async () => {
    try {
      console.log('🔍 Saving working hours for project:', effectiveProjectId);
      const entries = [];
      for (const workerId in editedHours) {
        for (const date in editedHours[workerId]) {
          const hoursWorked = editedHours[workerId][date];
          if (hoursWorked != null) {
            entries.push({ projectId: effectiveProjectId, workerId, date, hoursWorked });
          }
        }
      }

      const res = await authFetch.post('/working-hours/bulk', { entries });

      console.log('✅ Working hours saved successfully');
      alert(t('projectDetails.workingHoursSaved'));
      setEditedHours({});
      
      // Refresh working hours data
      const refreshed = await authFetch.get(`/working-hours?projectId=${effectiveProjectId}`);
      let hoursData;
      if (refreshed && refreshed.data) {
        hoursData = refreshed.data;
      } else {
        hoursData = refreshed;
      }

      const grouped = {};
      const allDates = new Set();
      hoursData.forEach(entry => {
        if (!grouped[entry.workerId]) grouped[entry.workerId] = {};
        grouped[entry.workerId][entry.date] = entry.hoursWorked;
        allDates.add(entry.date);
      });
      setWorkingHours(grouped);
      setHourLogDates(Array.from(allDates).sort());
    } catch (err) {
      console.error('❌ Error saving working hours:', err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
      alert(t('projectDetails.errorSavingHours'));
    }
  };

  const dates = Array.from(new Set(
    activities.flatMap(a => (a.dailyLog || []).filter(log => log && log.date).map(log => log.date))
  )).sort();

  if (loading) return <div className="p-6">{t('projectDetails.loadingProjectDetails')}</div>;

  // Debug information
  console.log('🔍 ProjectDetails Debug:', {
    projectId: effectiveProjectId,
    projectData: !!projectData,
    activities: activities.length,
    assignedWorkers: assignedWorkers.length,
    workingHours: Object.keys(workingHours).length,
    additionalWorks: additionalWorks.length,
    photos: photos.length,
    assignedEmployeeDetails
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center pt-16">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('projectDetails.projectDetails')}</h1>
          <p className="text-gray-600 mt-2">{t('projectDetails.viewManageDescription')}</p>
        </div>
        <div className="flex gap-2">
          {projectData && (
            <PDFDownloadLink
            document={
              <ProjectDetailsPDF
                project={projectData}
                activities={activities}
                additionalWorks={additionalWorks}
                assignedEmployee={assignedEmployeeDetails}
                assignedWorkers={assignedWorkers}
                workerDetails={workerDetails}
                workingHours={workingHours}
                hourLogDates={filteredDates}
                notificationMatrix={projectData?.notificationMatrix || []}
                dueDate={projectData?.dueDate}
                photos={photos}
              />
            }
            fileName={`${projectData?.name || 'project-details'}.pdf`}
          >
            {({ loading, error }) => {
              if (error) {
                console.error('PDF Generation Error:', error);
              }
              return loading ? (
                <button className="px-4 py-2 bg-gray-400 text-white rounded cursor-not-allowed">{t('projectDetails.generatingPDF')}</button>
              ) : error ? (
                <button className="px-4 py-2 bg-red-500 text-white rounded cursor-not-allowed" title={error.message}>
                  {t('projectDetails.exportFailed')}
                </button>
              ) : (
                <button className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">
                  {t('projectDetails.exportToPDF')}
                </button>
              );
            }}
          </PDFDownloadLink>
          )}
        </div>
      </div>

      {/* Project Info */}
      <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
        <h2 className="text-lg font-medium text-gray-900 mb-4">{t('projectDetails.projectInfo')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-6 text-sm">
          <div>
            <div className="font-medium text-gray-700">{t('projectDetails.projectName')}</div>
            <div className="text-gray-900">{projectData?.name}</div>
          </div>
          <div>
            <div className="font-medium text-gray-700">{t('projectDetails.percentCompleted')}</div>
            <div className="text-gray-900">{projectData?.percentCompleted || 0}%</div>
          </div>
          <div>
            <div className="font-medium text-gray-700">{t('projectDetails.status')}</div>
            <div className="text-gray-900">{projectData?.status}</div>
          </div>
          <div>
            <div className="font-medium text-gray-700">{t('projectDetails.assignedEmployee')}</div>
            <div className="text-gray-900">{assignedEmployeeDetails || 'N/A'}</div>
          </div>
          <div>
            <div className="font-medium text-gray-700">{t('projectDetails.assignedWorkers')}</div>
            <div className="text-gray-900">{assignedWorkers.length}</div>
          </div>
          <div>
            <div className="font-medium text-gray-700">{t('projectDetails.notificationMatrix')}</div>
            <div className="text-gray-900">{Array.isArray(projectData?.notificationThresholds) && projectData.notificationThresholds.length > 0 ? projectData.notificationThresholds.map(n => `${n}%`).join(', ') : '—'}</div>
          </div>
          <div>
            <div className="font-medium text-gray-700">{t('projectDetails.workingHours')}</div>
            <div className="text-gray-900">{Object.values(workingHours).reduce((total, workerHours) => total + Object.values(workerHours).reduce((sum, h) => sum + h, 0), 0)}</div>
          </div>
          <div>
            <div className="font-medium text-gray-700">{t('projectDetails.additionalWorkingHours')}</div>
            <div className="text-gray-900">{additionalWorks.reduce((sum, w) => sum + Number(w.hours || 0), 0)}</div>
          </div>
          <div>
            <div className="font-medium text-gray-700">{t('projectDetails.totalWorkingHours')}</div>
            <div className="text-gray-900">{Object.values(workingHours).reduce((total, workerHours) => total + Object.values(workerHours).reduce((sum, h) => sum + h, 0), 0) + additionalWorks.reduce((sum, w) => sum + Number(w.hours || 0), 0)}</div>
          </div>
          <div>
            <div className="font-medium text-gray-700">{t('projectDetails.firstLogDate')}</div>
            <div className="text-gray-900">{activities.length > 0 ? new Date(activities.flatMap((a) => a.dailyLog?.map((l) => l.date)).sort()[0]).toLocaleDateString('bg-BG') : '—'}</div>
          </div>
          <div>
            <div className="font-medium text-gray-700">{t('projectDetails.finishDate')}</div>
            <div className="text-gray-900">{projectData?.dueDate ? new Date(projectData.dueDate).toLocaleDateString('bg-BG') : '—'}</div>
          </div>
          <div>
            <div className="font-medium text-gray-700">{t('projectDetails.daysLeft')}</div>
            <div className="text-gray-900">{projectData?.dueDate ? Math.max(0, Math.ceil((new Date(projectData.dueDate) - new Date()) / (1000 * 60 * 60 * 24))) : '—'}</div>
          </div>
        </div>
      </div>




      {/* Activities */}
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900">{t('projectDetails.activities')}</h2>
          <div className="flex gap-4">
            <button
              onClick={() => setShowAssignModal(true)}
              className="px-4 py-2 bg-green-700 text-white rounded hover:bg-green-800 transition-colors"
            >
              {t('projectDetails.assignWorkers')}
            </button>
            <button onClick={handleSaveActivities} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">
              {t('projectDetails.saveActivities')}
            </button>
          </div>
        </div>

        {/* Mechanical Activities */}
        <ActivityCategoryBlock
          category="Mechanical"
          activities={activities.filter(act => act.category === 'Mechanical')}
          onAddActivity={handleAddActivity}
          onUpdateActivity={handleUpdateActivity}
          onDeleteActivity={handleDeleteActivity}
          title="🔧 Mechanical Activities"
        />

        {/* Electro Activities */}
        <ActivityCategoryBlock
          category="Electro"
          activities={activities.filter(act => act.category === 'Electro')}
          onAddActivity={handleAddActivity}
          onUpdateActivity={handleUpdateActivity}
          onDeleteActivity={handleDeleteActivity}
          title="⚡ Electro Activities"
        />
      </div>

      {showAssignModal && (
        <AssignWorkersModal 
          projectId={effectiveProjectId} 
          initialAssignedWorkers={assignedWorkers}
          translations={assignWorkersTranslations}
          onClose={() => setShowAssignModal(false)}
          onAssign={async (newWorkers) => {
            try {
              const uniqueWorkers = [...new Set([...assignedWorkers, ...newWorkers])];
              // Persist assigned workers on project
              await authFetch.post(`/projects/${effectiveProjectId}/assign-workers`, {
                workerIds: uniqueWorkers
              });
              setAssignedWorkers(uniqueWorkers);

              // Fetch all workers and update details
              const res = await authFetch.get("/workers");
              let allWorkers;
              if (res && res.data) {
                allWorkers = res.data;
              } else {
                allWorkers = res;
              }
              const updatedDetails = {};
              uniqueWorkers.forEach(id => {
                const match = allWorkers.find(w => w._id === id);
                if (match) updatedDetails[id] = match;
              });
              setWorkerDetails(updatedDetails);

              // Optionally, refresh working hours data to ensure new workers show up in the table
              const refreshed = await authFetch.get(`/working-hours?projectId=${effectiveProjectId}`);
              let hoursData;
              if (refreshed && refreshed.data) {
                hoursData = refreshed.data;
              } else {
                hoursData = refreshed;
              }
              const grouped = {};
              const allDates = new Set();
              hoursData.forEach(entry => {
                if (!grouped[entry.workerId]) grouped[entry.workerId] = {};
                grouped[entry.workerId][entry.date] = entry.hoursWorked;
                allDates.add(entry.date);
              });
              setWorkingHours(grouped);
              setHourLogDates(Array.from(allDates).sort());

              setShowAssignModal(false);
            } catch (err) {
              console.error('Failed to assign workers:', err);
            }
          }}
          onClose={() => setShowAssignModal(false)} 
        />
      )}

      {/* Daily Logs Section */}
      <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
        <div className="p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">{t('projectDetails.dailyLogs')}</h2>
          {dates.length === 0 ? (
            <p className="text-gray-500">{t('projectDetails.noLogsAvailable')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('projectDetails.date')}</th>
                    {activities.map((act, i) => (
                      <th key={i} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{act.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {dates.map(date => (
                    <tr key={date} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(date).toLocaleDateString('bg-BG')} {new Date(date).toLocaleTimeString('bg-BG', { hour: '2-digit', minute: '2-digit' })}
                      </td>
                      {activities.map((act, i) => {
                        const log = act.dailyLog?.find(l => l.date === date);
                        return (
                          <td key={i} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className={`block px-2 py-1 rounded text-center font-medium ${
                              log?.quantity >= act.dailyTarget ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {log?.quantity ?? 0}
                              {log?.quantity > act.dailyTarget ? ` (+${log.quantity - act.dailyTarget})` : ''}
                              {log?.quantity < act.dailyTarget ? ` (-${act.dailyTarget - log.quantity})` : ''}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Working Hours Section */}
      <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
        <div className="p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">{t('projectDetails.workingHoursLogs')}</h2>
          <button
            onClick={() => setShowLogModal(true)}
            className="mb-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            {t('projectDetails.logWorkingHours')}
          </button>
          {/* Date Range Picker */}
          <div className="flex gap-4 items-center mb-4">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <span>{t('projectDetails.from')}</span>
              <input
                type="date"
                value={startDate}
                max={endDate || ''}
                onChange={e => setStartDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <span>{t('projectDetails.to')}</span>
              <input
                type="date"
                value={endDate}
                min={startDate || ''}
                onChange={e => setEndDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </label>
            <button
              onClick={() => {
                setStartDate('');
                setEndDate('');
              }}
              className="ml-4 px-3 py-1 bg-gray-300 rounded hover:bg-gray-400 transition-colors"
            >
              {t('projectDetails.clear')}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-blue-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('projectDetails.worker')}</th>
                {filteredDates.map(date => (
                  <th key={date} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{new Date(date).toLocaleDateString('bg-BG')}</th>
                ))}
              </tr>
            </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {assignedWorkers.map(workerId => {
                  const logs = workingHours[workerId] || {};
                  return (
                    <tr key={workerId} className="hover:bg-gray-50">
                      {/* Show worker name from workerDetails */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{workerDetails[workerId]?.name || 'Unknown'}</td>
                      {filteredDates.map(date => (
                        <td key={date} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                          <input
                            type="number"
                            value={
                              editedHours[workerId]?.[date] ??
                              logs[date] ??
                              ''
                            }
                            onChange={(e) => {
                              const value = Number(e.target.value);
                              setEditedHours((prev) => ({
                                ...prev,
                                [workerId]: {
                                  ...prev[workerId],
                                  [date]: value,
                                },
                              }));
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 bg-gray-50">{t('projectDetails.totalPerDay')}</td>
                  {filteredDates.map(date => {
                    const totalForDate = assignedWorkers.reduce((sum, workerId) => {
                      return sum + (workingHours[workerId]?.[date] || 0);
                    }, 0);
                    return (
                      <td key={date} className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-center bg-gray-50">
                        {totalForDate}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="mt-4 space-y-2">
            <p className="text-sm font-medium text-gray-700">{t('projectDetails.selectedDatesHours')} <span className="text-gray-900">{selectedDatesTotalHours}</span></p>
            <p className="text-sm font-medium text-gray-700">{t('projectDetails.grandTotalHours')} <span className="text-gray-900">{
              Object.values(workingHours).reduce((sum, worker) =>
                sum + Object.values(worker).reduce((wSum, h) => wSum + h, 0), 0
              )
            }</span></p>
          </div>
          <div className="mt-4">
            <button
              onClick={handleSaveHours}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              {t('projectDetails.saveWorkingHours')}
            </button>
          </div>
        </div>
        
        {/* Working Hours Modal */}
        {showLogModal && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-md w-full max-w-md">
              <h2 className="text-xl font-semibold mb-4">{t('projectDetails.logHoursForToday')}</h2>
              <div className="space-y-4">
                {assignedWorkers.map((workerId) => (
                  <div key={workerId}>
                    <label className="block mb-1 font-medium">
                      {workerDetails[workerId]?.name || 'Unknown'}:
                    </label>
                    <input
                      type="number"
                      min="0"
                      className="w-full border rounded px-3 py-2"
                      value={dailyInput[workerId] ?? ''}
                      onChange={(e) =>
                        setDailyInput((prev) => ({
                          ...prev,
                          [workerId]: Number(e.target.value),
                        }))
                      }
                    />
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => setShowLogModal(false)}
                  className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-400 transition-colors"
                >
                  {t('projectDetails.cancel')}
                </button>
                <button
                  onClick={async () => {
                    const today = new Date().toISOString().split('T')[0];
                    const entries = Object.entries(dailyInput)
                      .filter(([_, hours]) => hours !== '')
                      .map(([workerId, hoursWorked]) => ({
                        projectId: effectiveProjectId,
                        workerId,
                        date: today,
                        hoursWorked,
                      }));
                    // Append new working hours to existing hourLogDates
                    await authFetch.post('/working-hours/bulk', { entries });
                    setShowLogModal(false);
                    setDailyInput({});
                    // Refresh working hours and hourLogDates
                    const refreshed = await authFetch.get(`/working-hours?projectId=${effectiveProjectId}`);
                    let hoursData;
                    if (refreshed && refreshed.data) {
                      hoursData = refreshed.data;
                    } else {
                      hoursData = refreshed;
                    }
                    const grouped = {};
                    const allDates = new Set();
                    hoursData.forEach(entry => {
                      if (!grouped[entry.workerId]) grouped[entry.workerId] = {};
                      grouped[entry.workerId][entry.date] = entry.hoursWorked;
                      allDates.add(entry.date);
                    });
                    setWorkingHours(grouped);
                    setHourLogDates(Array.from(allDates).sort());
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  {t('projectDetails.save')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Additional Work Section */}
      <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
        <div className="p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">{t('projectDetails.additionalWork')}</h2>
          <div className="mb-4">
            <button
              onClick={() => setShowAddAdditionalModal(true)}
              className="bg-blue-600 text-white rounded hover:bg-blue-700 px-4 py-2 transition-colors"
            >
              {t('projectDetails.addAdditionalWork')}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-blue-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('projectDetails.description')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('projectDetails.hours')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('projectDetails.comments')}</th>
              </tr>
            </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {additionalWorks.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.title}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.hours}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.comment}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">{t('projectDetails.total')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{additionalWorks.reduce((sum, w) => sum + Number(w.hours || 0), 0)}</td>
                  <td className="px-6 py-4"></td>
                </tr>
              </tfoot>
            </table>
          </div>
          {showAddAdditionalModal && (
            <div className="modal fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
              <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
                <h2 className="text-xl font-semibold mb-4">{t('projectDetails.addAdditionalWorkModal')}</h2>
                <input
                  type="text"
                  placeholder={t('projectDetails.description')}
                  className="w-full border rounded p-2 mb-3"
                  value={newAdditionalWork.description}
                  onChange={(e) => setNewAdditionalWork({ ...newAdditionalWork, description: e.target.value })}
                />
                <input
                  type="number"
                  placeholder={t('projectDetails.hours')}
                  className="w-full border rounded p-2 mb-3"
                  value={newAdditionalWork.hours}
                  onChange={(e) => setNewAdditionalWork({ ...newAdditionalWork, hours: e.target.value })}
                />
                <textarea
                  placeholder={t('projectDetails.comments')}
                  className="w-full border rounded p-2 mb-3"
                  rows="3"
                  value={newAdditionalWork.comment}
                  onChange={(e) => setNewAdditionalWork({ ...newAdditionalWork, comment: e.target.value })}
                />
                <div className="flex justify-end space-x-2">
                  <button onClick={() => setShowAddAdditionalModal(false)} className="px-4 py-2 bg-gray-200 rounded">{t('projectDetails.cancel')}</button>
                  <button
                    onClick={async () => {
                      const payload = [{
                        projectId: effectiveProjectId,
                        title: newAdditionalWork.description,
                        hours: Number(newAdditionalWork.hours),
                        comment: newAdditionalWork.comment
                      }];
                      await authFetch.post('/additionalwork/bulk', { entries: payload });
                      const refreshed = await authFetch.get(`/additionalwork?projectId=${effectiveProjectId}`);
                      let updatedData;
                      if (refreshed && refreshed.data) {
                        updatedData = refreshed.data;
                      } else {
                        updatedData = refreshed;
                      }
                      setAdditionalWorks(updatedData || []);
                      setNewAdditionalWork({ description: '', hours: '', comment: '' });
                      setShowAddAdditionalModal(false);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    {t('projectDetails.add')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Project Photos Section */}
      <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
        <div className="p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">{t('projectDetails.projectPhotos')}</h2>
          <input type="file" multiple onChange={handlePhotoUpload} className="mb-4" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {photos.map((src, index) => (
              <img
                key={index}
                src={src}
                alt={`${t('projectDetails.uploaded')} ${index}`}
                className="rounded-xl cursor-pointer object-cover w-full h-64"
                onClick={() => setSelectedPhoto(src)}
              />
            ))}
          </div>
          {selectedPhoto && (
            <div
              className="fixed inset-0 z-50 bg-black bg-opacity-80 flex justify-center items-center"
              onClick={() => setSelectedPhoto(null)}
            >
              <img src={selectedPhoto} alt="Full size" className="max-w-full max-h-full rounded" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectDetails;
